// The app's URLs, so a reload or Back lands where the writer was:
//   /                                              the Library (every project; the sample project on an empty one)
//   /help[/<article>]                              Help (Apps/Shared/Help, bundled when the app is built)
//   /export                                        Export everything (`.writingsuite` packages, made in this browser)
//   /settings                                      Settings: this browser's appearance and writing preferences
//   /profile                                       Your profile: the account, this browser, usage, and Sign Out
//   /host-check                                    The host self-check (views/HostCheck.tsx): what this browser or native
//                                                  shell can do, for the Windows spike; read signed in or not
//   /p/<project>/<room>/<page>[/<node>][?in=<arrangement>]
//                                                  a room's page (rooms/rooms.ts, rooms/registry.ts); the node only on
//                                                  pages about an item (Write, Corkboard, Outliner); `in` only when
//                                                  the arrangement isn't the manuscript. A room with one tab leaves its
//                                                  page out: /p/<project>/write/<node>, /p/<project>/publish.
//   /p/<project>/<room>                            the room's last page (this browser's)
//   /p/<project>                                   the room last worked in there (this browser's), else Write
// A project shared with this account (W9a) is `<owner>~<project>` where a project id goes: its address
// `<owner>/<project>` (client/net/address.ts) with the `/` written as `~`, which no id contains.
// Before the rooms (W2–W6) pages had their own names; those URLs redirect: /p/<project> → Write, read → Write,
// cards → Plan ▸ Corkboard, outline → Plan ▸ Outliner, comments/history/readers → Revise, stats → Library ▸ All
// statistics, agents → Library ▸ Agents on the web. The Story room's pages are Plan's now: /story/<page> → /plan/<page>.
import { useSyncExternalStore } from 'react';
import { registry } from '../rooms/registry.ts';
import { isRoom, WORKING_ROOMS, type RoomId } from '../rooms/rooms.ts';

export type ProjectRoute = {
  name: 'project';
  projectId: string;
  room: RoomId;
  page: string;
  nodeId: string | null;
  arrangementId: string | null;
  /** Other query parameters a page reads (a column's choice, say), kept in its links. */
  params: Record<string, string>;
};

export type Route =
  | { name: 'library' }
  | { name: 'help'; article: string | null }
  | { name: 'export' }
  | { name: 'settings' }
  | { name: 'profile' }
  | { name: 'hostCheck' }
  | ProjectRoute
  | { name: 'redirect'; to: string }
  | { name: 'missing'; path: string };

const ID = /^[A-Za-z0-9_.:-]{1,128}$/;
/** A project in a URL: an id, or a shared project's `<owner>~<project>`. */
const PROJECT = /^[A-Za-z0-9_.:-]{1,128}(?:~[A-Za-z0-9_-]{1,64})?$/;

/** A project's address as its URL segment (`owner/project` → `owner~project`). */
export const projectSegment = (address: string) => encodeURIComponent(address.replace('/', '~'));

/** The project's own path: `/p/<segment>`. */
export const projectPath = (address: string) => `/p/${projectSegment(address)}`;
const ARTICLE = /^[a-z0-9-]{1,64}$/;
const PARAM = /^[A-Za-z0-9_.:-]{1,128}$/;

/** The pages before the rooms, and where they went. */
export const LEGACY: Record<string, { room: RoomId; page: string }> = {
  read: { room: 'write', page: 'write' },
  cards: { room: 'plan', page: 'corkboard' },
  outline: { room: 'plan', page: 'outliner' },
  comments: { room: 'revise', page: 'comments' },
  history: { room: 'revise', page: 'history' },
  readers: { room: 'revise', page: 'readers' },
  stats: { room: 'library', page: 'statistics' },
  agents: { room: 'library', page: 'agents' },
};

/** Rooms that were, and where their pages went (the Story room folded into Plan, 2026-10-07). */
export const LEGACY_ROOMS: Record<string, RoomId> = { story: 'plan' };

/** The page a room last showed in this browser (its tabs remember it), if it still exists. */
export type LastPage = (room: RoomId) => string | null;

/** The room a project was last worked in, in this browser. */
export type LastRoom = (projectId: string) => RoomId | null;

function queryOf(search: string): { arrangementId: string | null; params: Record<string, string> } {
  const q = new URLSearchParams(search);
  const params: Record<string, string> = {};
  let arrangementId: string | null = null;
  for (const [k, v] of q) {
    if (k === 'in') arrangementId = ID.test(v) ? v : null;
    else if (PARAM.test(k) && PARAM.test(v)) params[k] = v;
  }
  return { arrangementId, params };
}

export function parseRoute(pathname: string, search = '', lastPage: LastPage = readLastPage, lastRoom: LastRoom = readLastRoom): Route {
  const parts = pathname
    .split('/')
    .filter(Boolean)
    .map((s) => {
      try {
        return decodeURIComponent(s);
      } catch {
        return '';
      }
    });
  if (parts.length === 0) return { name: 'library' };
  if (parts[0] === 'export' && parts.length === 1) return { name: 'export' };
  if (parts[0] === 'settings' && parts.length === 1) return { name: 'settings' };
  if (parts[0] === 'profile' && parts.length === 1) return { name: 'profile' };
  if (parts[0] === 'host-check' && parts.length === 1) return { name: 'hostCheck' };
  if (parts[0] === 'help' && parts.length <= 2) return { name: 'help', article: parts[1] && ARTICLE.test(parts[1]) ? parts[1] : null };
  if (parts[0] !== 'p' || !parts[1] || !PROJECT.test(parts[1]) || parts.length > 5) return { name: 'missing', path: pathname };
  const projectId = parts[1].replace('~', '/');
  const { arrangementId, params } = queryOf(search);
  const reg = registry();
  const at = (room: RoomId, page: string, node: string | undefined): ProjectRoute => {
    const def = reg.find(room, page);
    return { name: 'project', projectId, room, page, nodeId: def?.takesNode && node && ID.test(node) ? node : null, arrangementId, params };
  };
  /** The page a room opens on by itself: the one it last showed here, else its first (never a hidden one). */
  const landing = (room: RoomId) => {
    const last = lastPage(room);
    return last && reg.find(room, last) && !reg.find(room, last)!.hidden ? last : reg.first(room).page;
  };
  if (parts.length === 2) {
    const room = lastRoom(projectId) ?? 'write';
    return { name: 'redirect', to: formatRoute(at(room, landing(room), undefined)) };
  }
  const segment = parts[2];
  const legacy = LEGACY[segment];
  if (legacy && parts.length <= 4) return { name: 'redirect', to: formatRoute(at(legacy.room, legacy.page, parts[3])) };
  const legacyRoom = LEGACY_ROOMS[segment];
  if (legacyRoom && parts.length <= 4) {
    const page = parts[3] && reg.find(legacyRoom, parts[3]) ? parts[3] : landing(legacyRoom);
    return { name: 'redirect', to: formatRoute(at(legacyRoom, page, undefined)) };
  }
  if (!isRoom(segment)) return { name: 'missing', path: pathname };
  const room = segment;
  const rest = parts.slice(3);
  if (rest[0] && reg.find(room, rest[0])) {
    if (rest.length > 2) return { name: 'missing', path: pathname };
    return at(room, rest[0], rest[1]);
  }
  if (reg.singlePage(room)) {
    if (rest.length > 1) return { name: 'missing', path: pathname };
    return at(room, reg.first(room).page, rest[0]);
  }
  if (rest.length === 0) return { name: 'redirect', to: formatRoute(at(room, landing(room), undefined)) };
  return { name: 'missing', path: pathname };
}

export function formatRoute(r: Route): string {
  if (r.name === 'library') return '/';
  if (r.name === 'export') return '/export';
  if (r.name === 'settings') return '/settings';
  if (r.name === 'profile') return '/profile';
  if (r.name === 'hostCheck') return '/host-check';
  if (r.name === 'help') return r.article ? `/help/${encodeURIComponent(r.article)}` : '/help';
  if (r.name === 'redirect') return r.to;
  if (r.name === 'missing') return r.path;
  const reg = registry();
  const def = reg.find(r.room, r.page);
  const single = reg.singlePage(r.room) && reg.first(r.room).page === r.page;
  let path = `${projectPath(r.projectId)}/${r.room}${single ? '' : `/${r.page}`}`;
  if (r.nodeId && def?.takesNode) path += `/${encodeURIComponent(r.nodeId)}`;
  const q = new URLSearchParams();
  if (r.arrangementId) q.set('in', r.arrangementId);
  for (const [k, v] of Object.entries(r.params ?? {})) q.set(k, v);
  const query = q.toString();
  return query ? `${path}?${query}` : path;
}

/**
 * A page named for a link: "room/page", a room (its last page), or one of the old page names ("cards", "stats"…).
 * Null when there's no such page.
 */
export function resolvePage(ref: string, lastPage: LastPage = readLastPage): { room: RoomId; page: string } | null {
  const reg = registry();
  const slash = ref.indexOf('/');
  if (slash > 0) {
    const room = ref.slice(0, slash);
    const page = ref.slice(slash + 1);
    return isRoom(room) && reg.find(room, page) ? { room, page } : null;
  }
  if (isRoom(ref)) {
    const last = lastPage(ref);
    const def = last ? reg.find(ref, last) : null;
    return { room: ref, page: def && !def.hidden ? def.page : reg.first(ref).page };
  }
  return LEGACY[ref] ?? null;
}

// The rooms' last pages (per browser: a view choice, never synced) --------------------------------------------------

const LAST_PAGES = 'ws:roomPages';

/** Read once per page load (every room tab asks on every render), then kept here as it changes. */
let lastPages: Record<string, string> | null = null;

function readLastPages(): Record<string, string> {
  if (lastPages) return lastPages;
  try {
    const v = JSON.parse(localStorage.getItem(LAST_PAGES) ?? '{}');
    lastPages = v && typeof v === 'object' ? v : {};
  } catch {
    lastPages = {};
  }
  return lastPages!;
}

export function readLastPage(room: RoomId): string | null {
  const v = readLastPages()[room];
  return typeof v === 'string' ? v : null;
}

/** Remembers the page a room shows now, so choosing the room again returns to it. */
export function rememberPage(room: RoomId, page: string) {
  const all = readLastPages();
  if (all[room] === page) return;
  lastPages = { ...all, [room]: page };
  try {
    localStorage.setItem(LAST_PAGES, JSON.stringify(lastPages));
  } catch {
    // kept for this page only: nothing to keep it in
  }
}

// The room each project was last worked in (per browser; `/p/<project>` returns there) ----------------------------------

const LAST_ROOMS = 'ws:lastRooms';

let lastRooms: Record<string, string> | null = null;

function readLastRooms(): Record<string, string> {
  if (lastRooms) return lastRooms;
  try {
    const v = JSON.parse(localStorage.getItem(LAST_ROOMS) ?? '{}');
    lastRooms = v && typeof v === 'object' ? v : {};
  } catch {
    lastRooms = {};
  }
  return lastRooms!;
}

export function readLastRoom(projectId: string): RoomId | null {
  const v = readLastRooms()[projectId];
  return typeof v === 'string' && isRoom(v) && WORKING_ROOMS.includes(v) ? v : null;
}

/** Remembers the room a project is being worked in (never the Library), so the project opens there next time. */
export function rememberRoom(projectId: string, room: RoomId) {
  if (!WORKING_ROOMS.includes(room)) return;
  const all = readLastRooms();
  if (all[projectId] === room) return;
  lastRooms = { ...all, [projectId]: room };
  try {
    localStorage.setItem(LAST_ROOMS, JSON.stringify(lastRooms));
  } catch {
    // kept for this page only
  }
}

// Navigation ---------------------------------------------------------------------------------------------------------

const listeners = new Set<() => void>();
let current = typeof location === 'undefined' ? '/' : location.pathname + location.search;

/** The last page outside Help this tab showed (Help's Back returns to it). */
let outsideHelp: string | null = null;

export function pageBeforeHelp(): string {
  return outsideHelp ?? '/';
}

/** The last page outside Help, Settings and the profile (their Back returns to it). */
let outsideAccount: string | null = null;

const isAccountPage = (href: string) => /^\/(settings|profile)(?:[/?]|$)/.test(href);

/** Where Settings and the profile go back to: the page the writer came from (a project, or the Library). */
export function pageBeforeSettings(): { href: string; label: string } {
  const href = outsideAccount ?? '/';
  return { href, label: href.startsWith('/p/') ? 'Back to the project' : href === '/' ? 'Back to the Library' : 'Back' };
}

function changed() {
  if (!current.startsWith('/help')) outsideHelp = current;
  if (!current.startsWith('/help') && !isAccountPage(current)) outsideAccount = current;
  current = location.pathname + location.search;
  for (const fn of listeners) fn();
}

if (typeof window !== 'undefined') window.addEventListener('popstate', changed);

export function navigate(href: string, opts: { replace?: boolean } = {}) {
  if (href === location.pathname + location.search) return;
  if (opts.replace) history.replaceState(null, '', href);
  else history.pushState(null, '', href);
  changed();
}

export function useRoute(): Route {
  const href = useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => current,
  );
  const q = href.indexOf('?');
  return parseRoute(q < 0 ? href : href.slice(0, q), q < 0 ? '' : href.slice(q));
}

/** A plain click on a link: handled in the page, not by the browser (modifier keys still open tabs). */
export function followLink(event: { button: number; metaKey: boolean; ctrlKey: boolean; shiftKey: boolean; altKey: boolean; preventDefault(): void }, href: string) {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  navigate(href);
}
